import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { employeesAPI, workflowAPI, documentTemplatesAPI, signaturesAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { UserCheck, Loader2, ListChecks, FileText, PenLine } from 'lucide-react';

/**
 * Übernahme-Dialog: Anwärter -> aktiver Mitarbeiter.
 * Erfasst die Beschäftigungsdaten und startet optional den On-Boarding-Workflow
 * (Betriebsrat-Schreiben, Arbeitsvertrag, IT-Zugänge, ...).
 */
export const HireEmployeeDialog = ({ open, onOpenChange, employee, onSuccess }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [form, setForm] = useState({
    employment_start: new Date().toISOString().slice(0, 10),
    employment_type: 'Vollzeit',
    is_temporary: false,
    employment_end: '',
    position: '',
    department: '',
    weekly_hours: '',
    vacation_days_per_year: '',
    employee_number: '',
    trigger_onboarding: true,
    onboarding_template_id: '',
    arbeitsvertrag_template_id: 'none',
    betriebsrat_template_id: 'none',
    send_for_signature: false,
    employer_signer_name: '',
    employee_email: '',
  });

  useEffect(() => {
    if (!open || !employee) return;
    setForm((f) => ({
      ...f,
      position: employee.position || '',
      department: employee.department || '',
      weekly_hours: employee.weekly_hours != null ? String(employee.weekly_hours) : '',
      vacation_days_per_year: employee.vacation_days_per_year != null ? String(employee.vacation_days_per_year) : '',
      employee_number: employee.employee_number || '',
      employee_email: employee.email || employee.private_email || '',
      employer_signer_name: f.employer_signer_name
        || [user?.first_name, user?.last_name].filter(Boolean).join(' '),
    }));
    workflowAPI
      .getTemplates({ workflow_type: 'onboarding' })
      .then((res) => {
        const list = res.data || [];
        setTemplates(list);
        const def = list.find((t) => t.is_default) || list[0];
        if (def) setForm((f) => ({ ...f, onboarding_template_id: f.onboarding_template_id || def.id }));
      })
      .catch(() => setTemplates([]));
    documentTemplatesAPI
      .list()
      .then((res) => setDocTemplates((res.data || []).filter((t) => t.file_type === 'docx')))
      .catch(() => setDocTemplates([]));
  }, [open, employee, user?.first_name, user?.last_name]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const wantsContract = form.arbeitsvertrag_template_id && form.arbeitsvertrag_template_id !== 'none';

  const handleSubmit = async () => {
    if (wantsContract && form.send_for_signature) {
      if (!form.employer_signer_name.trim()) {
        toast.error('Bitte den Namen des Arbeitgeber-Unterzeichners angeben');
        return;
      }
      if (!form.employee_email.trim()) {
        toast.error('Bitte die E-Mail des Mitarbeiters für den Signatur-Link angeben');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        employment_start: form.employment_start || null,
        employment_type: form.employment_type || null,
        is_temporary: form.is_temporary,
        employment_end: form.is_temporary ? (form.employment_end || null) : null,
        position: form.position || null,
        department: form.department || null,
        weekly_hours: form.weekly_hours !== '' ? parseFloat(form.weekly_hours) : null,
        vacation_days_per_year: form.vacation_days_per_year !== '' ? parseInt(form.vacation_days_per_year, 10) : null,
        employee_number: form.employee_number || null,
        trigger_onboarding: form.trigger_onboarding,
        onboarding_template_id: form.trigger_onboarding ? (form.onboarding_template_id || null) : null,
        arbeitsvertrag_template_id: wantsContract ? form.arbeitsvertrag_template_id : null,
        betriebsrat_template_id: form.betriebsrat_template_id && form.betriebsrat_template_id !== 'none'
          ? form.betriebsrat_template_id : null,
      };
      const res = await employeesAPI.hire(employee.id, payload);
      const parts = [];
      if (res.data?.workflow_id) parts.push('On-Boarding gestartet');
      if (res.data?.contract_document_id) parts.push('Arbeitsvertrag erzeugt');
      if (res.data?.betriebsrat_document_id) parts.push('Betriebsrat-Schreiben erzeugt');

      let signatureStarted = false;
      let signatureFailed = false;
      if (wantsContract && form.send_for_signature && res.data?.contract_document_id) {
        try {
          await signaturesAPI.createWorkflow({
            document_id: res.data.contract_document_id,
            base_url: window.location.origin,
            parties: [
              { role_label: 'Arbeitgeber', signer_name: form.employer_signer_name.trim(), method: 'in_app', email: null },
              {
                role_label: 'Arbeitnehmer',
                signer_name: `${employee.first_name} ${employee.last_name}`.trim(),
                method: 'link',
                email: form.employee_email.trim(),
              },
            ],
          });
          signatureStarted = true;
          parts.push('zur Unterschrift gesendet');
        } catch (sigErr) {
          signatureFailed = true;
        }
      }

      const baseMsg = parts.length ? `Mitarbeiter übernommen – ${parts.join(', ')}` : 'Mitarbeiter erfolgreich übernommen';
      if (signatureFailed) {
        toast.warning(`${baseMsg}. Hinweis: Der Signatur-Versand ist fehlgeschlagen – bitte den Vertrag manuell zur Unterschrift senden.`);
      } else {
        toast.success(baseMsg);
      }
      onOpenChange(false);
      onSuccess?.({ ...res.data, signatureStarted });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Übernahme fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="hire-employee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-primary" />
            Anwärter übernehmen
          </DialogTitle>
          <DialogDescription>
            {employee.first_name} {employee.last_name} wird als aktiver Mitarbeiter übernommen.
            Ergänze die Beschäftigungsdaten und starte optional das On-Boarding.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="hire-start">Eintrittsdatum</Label>
              <Input
                id="hire-start" type="date" value={form.employment_start}
                onChange={(e) => set('employment_start', e.target.value)}
                data-testid="hire-employment-start"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hire-number">Personalnummer</Label>
              <Input
                id="hire-number" value={form.employee_number}
                onChange={(e) => set('employee_number', e.target.value)}
                placeholder="z. B. P-1234"
                data-testid="hire-employee-number"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Beschäftigungsart</Label>
              <Select value={form.employment_type} onValueChange={(v) => set('employment_type', v)}>
                <SelectTrigger data-testid="hire-employment-type">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vollzeit">Vollzeit</SelectItem>
                  <SelectItem value="Teilzeit">Teilzeit</SelectItem>
                  <SelectItem value="Minijob">Minijob</SelectItem>
                  <SelectItem value="Werkstudent">Werkstudent</SelectItem>
                  <SelectItem value="Auszubildende/r">Auszubildende/r</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hire-hours">Wochenstunden</Label>
              <Input
                id="hire-hours" type="number" step="0.5" value={form.weekly_hours}
                onChange={(e) => set('weekly_hours', e.target.value)}
                placeholder="z. B. 40"
                data-testid="hire-weekly-hours"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hire-position">Einsatz als</Label>
              <Input
                id="hire-position" value={form.position}
                onChange={(e) => set('position', e.target.value)}
                data-testid="hire-position"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hire-vacation">Urlaubstage/Jahr</Label>
              <Input
                id="hire-vacation" type="number" value={form.vacation_days_per_year}
                onChange={(e) => set('vacation_days_per_year', e.target.value)}
                placeholder="z. B. 30"
                data-testid="hire-vacation-days"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border/60 p-3">
            <div>
              <p className="text-sm font-medium">Befristetes Arbeitsverhältnis</p>
              <p className="text-xs text-muted-foreground">Bei Befristung ein Enddatum angeben</p>
            </div>
            <Switch
              checked={form.is_temporary}
              onCheckedChange={(v) => set('is_temporary', v)}
              data-testid="hire-is-temporary"
            />
          </div>
          {form.is_temporary && (
            <div className="space-y-1.5">
              <Label htmlFor="hire-end">Befristet bis</Label>
              <Input
                id="hire-end" type="date" value={form.employment_end}
                onChange={(e) => set('employment_end', e.target.value)}
                data-testid="hire-employment-end"
              />
            </div>
          )}

          <div className="rounded-lg border border-border/60 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-sm font-medium">On-Boarding-Workflow starten</p>
                  <p className="text-xs text-muted-foreground">
                    Erzeugt Aufgaben: Betriebsrat-Schreiben, Arbeitsvertrag, IT-Zugänge …
                  </p>
                </div>
              </div>
              <Switch
                checked={form.trigger_onboarding}
                onCheckedChange={(v) => set('trigger_onboarding', v)}
                data-testid="hire-trigger-onboarding"
              />
            </div>
            {form.trigger_onboarding && templates.length > 0 && (
              <div className="space-y-1.5">
                <Label>Workflow-Vorlage</Label>
                <Select value={form.onboarding_template_id} onValueChange={(v) => set('onboarding_template_id', v)}>
                  <SelectTrigger data-testid="hire-template-select">
                    <SelectValue placeholder="Vorlage auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.trigger_onboarding && templates.length === 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Keine On-Boarding-Vorlage gefunden – der Workflow kann später manuell gestartet werden.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-border/60 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <div>
                <p className="text-sm font-medium">Arbeitsvertrag automatisch erzeugen</p>
                <p className="text-xs text-muted-foreground">
                  Aus einer Word-Vorlage mit den Mitarbeiterdaten – wird als PDF in der Akte
                  (Kategorie „Arbeitsvertrag") abgelegt und erledigt die Vertrags-Aufgabe.
                </p>
              </div>
            </div>
            <Select
              value={form.arbeitsvertrag_template_id}
              onValueChange={(v) => set('arbeitsvertrag_template_id', v)}
            >
              <SelectTrigger data-testid="hire-contract-template">
                <SelectValue placeholder="Keine – nur übernehmen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nicht automatisch erzeugen</SelectItem>
                {docTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {docTemplates.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Keine Word-Vorlagen vorhanden. Lade unter „Dokumente → Vorlagen" eine Arbeitsvertrags-Vorlage hoch.
              </p>
            )}

            {wantsContract && (
              <div className="mt-2 rounded-md bg-muted/40 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PenLine className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium">Direkt zur Unterschrift senden</p>
                      <p className="text-xs text-muted-foreground">
                        Startet eine Zwei-Parteien-Signatur (Arbeitgeber in der App, Mitarbeiter per Link).
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={form.send_for_signature}
                    onCheckedChange={(v) => set('send_for_signature', v)}
                    data-testid="hire-send-signature"
                  />
                </div>
                {form.send_for_signature && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="hire-employer-signer">Arbeitgeber (Unterzeichner)</Label>
                      <Input
                        id="hire-employer-signer" value={form.employer_signer_name}
                        onChange={(e) => set('employer_signer_name', e.target.value)}
                        placeholder="Name der/des Unterzeichnenden"
                        data-testid="hire-employer-signer"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="hire-employee-email">E-Mail Mitarbeiter (Signatur-Link)</Label>
                      <Input
                        id="hire-employee-email" type="email" value={form.employee_email}
                        onChange={(e) => set('employee_email', e.target.value)}
                        placeholder="mitarbeiter@example.de"
                        data-testid="hire-employee-email"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-border/60 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <div>
                <p className="text-sm font-medium">Betriebsrat-Anhörungsschreiben erzeugen</p>
                <p className="text-xs text-muted-foreground">
                  Aus einer Word-Vorlage – wird als PDF in der Akte abgelegt und erledigt die
                  Betriebsrat-Aufgabe im On-Boarding.
                </p>
              </div>
            </div>
            <Select
              value={form.betriebsrat_template_id}
              onValueChange={(v) => set('betriebsrat_template_id', v)}
            >
              <SelectTrigger data-testid="hire-betriebsrat-template">
                <SelectValue placeholder="Keine – nicht erzeugen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nicht automatisch erzeugen</SelectItem>
                {docTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="hire-cancel-btn">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="hire-confirm-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserCheck className="mr-2 h-4 w-4" />}
            Übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HireEmployeeDialog;
